import React, { useState } from 'react';

import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';

import * as AuthRequests from 'common/auth/AuthRequests';
import AuthButtonV2 from 'common/AuthButtonV2';
import ErrorMessage from 'common/ErrorMessage';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import { Separator } from 'common/ui/Separator';
import { Span } from 'common/ui/Text';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import getFirstName from 'common/util/getFirstName';
import validateInput from 'common/validateInput';

import RegistrationWrapper from './RegistrationWrapper';

import type { RegisterStepProps } from './types';
import type { ApiError } from 'common/util/parseAPIResponse';

type RegisterFormProps = RegisterStepProps & {
  referrer: { name: string };
};

const RegisterForm = ({ referrer, trialPlan, onSuccess, onContinue }: RegisterFormProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const emailValid = validateInput.email(email);

  const onOAuthSuccess = async (oauthData: AuthRequests.LoginParams) => {
    const { error, parsedResponse, redirecting } = await AuthRequests.login(oauthData);
    const apiError = error as ApiError;

    if (apiError && apiError.type === 'slow down') {
      setError(apiError.message);
      return;
    }

    if (redirecting) {
      return;
    }

    if (parsedResponse?.user) {
      const { companies } = parsedResponse.user;
      if (companies.length > 0) {
        onSuccess({ companies });
        return;
      }
    }

    onContinue({ type: 'oauth', loginParams: oauthData, email: oauthData.email });
  };

  const onOAuthFailure = (error: string) => setError(error);

  const getHeader = () =>
    referrer
      ? `${getFirstName(referrer.name)} has invited you to Canny!`
      : `Easily gather, track, and manage user feedback`;

  const getDescription = () => {
    const subheader = referrer ? (
      <>Get 20% off your monthly plan for 3 months or 5% off your first year of an annual plan.</>
    ) : (
      <>Get started with Canny for&nbsp;free, no credit card&nbsp;required</>
    );

    if (trialPlan === 'growth') {
      return <>Start your Canny Growth trial for&nbsp;free, no credit card&nbsp;required</>;
    } else if (trialPlan === 'starter') {
      return <>Try your Canny Starter trial for&nbsp;free, no credit card&nbsp;required</>;
    } else if (trialPlan === 'core') {
      return <>Try your Canny Core trial for&nbsp;free, no credit card&nbsp;required</>;
    } else if (trialPlan === 'pro') {
      return <>Try your Canny Pro trial for&nbsp;free, no credit card&nbsp;required</>;
    }

    return subheader;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!emailValid) {
      return;
    }

    onContinue({ type: 'email', email });
  };

  return (
    <RegistrationWrapper
      action={
        <Flex className="items-center gap-2">
          <p>Already have an account?</p>
          <Link to="/login">
            <Button variant="plain">Sign in</Button>
          </Link>
        </Flex>
      }
      className="wrapper-slot:max-w-[500px]">
      <RegistrationWrapper.Header>
        <RegistrationWrapper.Title>{getHeader()}</RegistrationWrapper.Title>
        <RegistrationWrapper.Description>{getDescription()}</RegistrationWrapper.Description>
      </RegistrationWrapper.Header>
      <RegistrationWrapper.Content>
        <AuthButtonV2
          authType="google"
          className="!w-full"
          onFailure={onOAuthFailure}
          onSuccess={onOAuthSuccess}
          size="large">
          Sign up with Google
        </AuthButtonV2>
        <HStack className="items-center gap-4">
          <Separator className="flex-1" />
          <Span className="text-secondary-foreground">or</Span>
          <Separator className="flex-1" />
        </HStack>
        <form onSubmit={handleSubmit}>
          <Flex className="gap-2 flex-col sm:flex-row">
            <Field>
              <Field.Label htmlFor="work-email">Work Email</Field.Label>
              <Field.Content>
                <Input
                  id="work-email"
                  onChange={setEmail}
                  placeholder="Enter your email"
                  autoFocus
                  size="large"
                  value={email}
                />
              </Field.Content>
            </Field>
            <Button
              color="primary"
              size="large"
              className="self-end !w-full sm:!w-auto"
              disabled={!emailValid}
              type="submit">
              Continue <ArrowRight />
            </Button>
          </Flex>
        </form>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </RegistrationWrapper.Content>
    </RegistrationWrapper>
  );
};

export default RegisterForm;
